// Gestionnaire d'erreurs pour PRIVIA YACHTS
// Gère les conflits avec les extensions de navigateur
import React from 'react';

// Fonction pour capturer et ignorer les erreurs d'extensions
export const setupErrorHandling = () => {
  // Capturer les erreurs globales
  window.addEventListener('error', (event) => {
    // Ignorer les erreurs provenant des extensions de navigateur
    if (
      event.filename?.includes('extension://') ||
      event.filename?.includes('content.js') ||
      event.filename?.includes('inject.js') ||
      event.message?.includes('Cannot redefine property') ||
      event.message?.includes('__s@3s') ||
      event.message?.includes('A213D+3')
    ) {
      console.warn('🔧 Erreur d\'extension de navigateur ignorée:', event.message);
      event.preventDefault();
      return false;
    }
    
    // Logger les vraies erreurs de l'application
    console.error('❌ Erreur application:', {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      error: event.error
    });
  });

  // Capturer les promesses rejetées
  window.addEventListener('unhandledrejection', (event) => {
    // Ignorer les erreurs d'extensions
    if (
      event.reason?.message?.includes('Cannot redefine property') ||
      event.reason?.message?.includes('extension') ||
      event.reason?.stack?.includes('content.js')
    ) {
      console.warn('🔧 Promise rejetée par extension ignorée:', event.reason);
      event.preventDefault();
      return false;
    }
    
    console.error('❌ Promise rejetée:', event.reason);
  });

  console.log('✅ Gestionnaire d\'erreurs PRIVIA YACHTS initialisé');
};

// Error Boundary Component pour React
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    // Ignorer les erreurs d'extensions
    if (
      error.message?.includes('Cannot redefine property') ||
      error.message?.includes('__s@3s') ||
      error.stack?.includes('content.js')
    ) {
      console.warn('🔧 Erreur d\'extension ignorée dans ErrorBoundary:', error.message);
      return { hasError: false };
    }
    
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Logger seulement les vraies erreurs
    if (!error.message?.includes('Cannot redefine property')) {
      console.error('❌ ErrorBoundary a capturé une erreur:', error, errorInfo);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-warm-gray-50">
          <div className="text-center p-8">
            <div className="w-16 h-16 mx-auto mb-6 bg-charcoal-900 rounded-full flex items-center justify-center">
              <span className="text-white text-2xl">⚠️</span>
            </div>
            <h1 className="text-2xl font-light text-charcoal-900 mb-4">
              Une erreur est survenue
            </h1>
            <p className="text-charcoal-600 mb-6 max-w-md mx-auto">
              Nous nous excusons pour ce désagrément. Veuillez rafraîchir la page ou contacter notre support.
            </p>
            <button 
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-charcoal-900 text-white text-sm font-light tracking-wide hover:bg-charcoal-800 transition-all"
            >
              Rafraîchir la page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Fonction pour détecter les extensions problématiques
export const detectProblematicExtensions = () => {
  const extensions = [];
  
  // Vérifier les variables globales communes des extensions
  const commonExtensionVars = [
    '__REACT_DEVTOOLS_GLOBAL_HOOK__',
    '__REDUX_DEVTOOLS_EXTENSION__',
    'chrome',
    'browser',
    '__s@3s',
    'A213D+3'
  ];
  
  commonExtensionVars.forEach(varName => {
    if (window[varName]) {
      extensions.push(varName);
    }
  });
  
  if (extensions.length > 0) {
    console.log('🔧 Extensions détectées:', extensions);
    console.log('💡 Si vous rencontrez des erreurs, essayez de désactiver temporairement vos extensions.');
  }
  
  return extensions;
};

export default { setupErrorHandling, ErrorBoundary, detectProblematicExtensions };
